import React, { useState, useEffect, useRef, useCallback } from 'react';
import { relationsApi } from '../../api/relations';
import { recordsApi } from '../../api/records';
import { adminApi } from '../../api/admin';
import { tagsApi } from '../../api/tags';
import ScreenRenderer from '../screens/ScreenRenderer';
import { Search, X, ExternalLink, Plus, Save, Loader, Tag } from 'lucide-react';
import toast from 'react-hot-toast';

function CreateModal({ targetTypeId, targetTypeName, onCreated, onClose }) {
  const [fields, setFields] = useState([]);
  const [screenLayout, setScreenLayout] = useState(null);
  const [fieldData, setFieldData] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [allTags, setAllTags] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [showTagPicker, setShowTagPicker] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const [fieldsRes, screensRes, tagsRes] = await Promise.all([
          adminApi.getFields(targetTypeId),
          adminApi.getScreens(targetTypeId),
          tagsApi.list(),
        ]);
        const fieldList = (fieldsRes.data?.fields || fieldsRes.fields || []).filter(f => !f.deleted_at);
        setFields(fieldList);

        const screens = screensRes.data?.screens || screensRes.screens || [];
        const defaultScreen = screens.find(s => s.is_default) || screens[0];
        if (defaultScreen?.layout) setScreenLayout(defaultScreen.layout);

        setAllTags((tagsRes.tags || []).filter(t => 
          t.category === (targetTypeName?.toLowerCase() || 'general') || t.category === 'general'
        ));
      } catch (err) {
        toast.error('Failed to load form');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [targetTypeId, targetTypeName]);

  const handleFieldChange = (fieldId, value) => {
    setFieldData(prev => ({ ...prev, [fieldId]: value }));
  };

  const handleSave = async () => {
    const required = fields.filter(f => f.is_required);
    for (const f of required) {
      if (!fieldData[f.id] && fieldData[f.id] !== 0) {
        toast.error(f.label + ' is required');
        return;
      }
    }
    setSaving(true);
    try {
      const res = await recordsApi.create({
        record_type_id: targetTypeId,
        field_data: fieldData,
      });
      const record = res.data?.record || res.record;

      // Apply tags
      for (const tagId of selectedTags) {
        try { await tagsApi.addToRecord(record.id, tagId); } catch(e) {}
      }

      toast.success((targetTypeName || 'Record') + ' created');
      onCreated(record);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to create record');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] flex flex-col animate-modal-in">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center gap-2">
            <Plus className="h-5 w-5 text-brand-600" />
            <h2 className="text-lg font-semibold text-gray-900">New {targetTypeName || 'Record'}</h2>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader className="h-5 w-5 animate-spin text-gray-400" />
            </div>
          ) : (
            <>
              {/* Tags */}
              <div className="mb-4 pb-4 border-b border-gray-100">
                <div className="flex flex-wrap items-center gap-1.5">
                  {selectedTags.map(tagId => {
                    const tag = allTags.find(t => t.id === tagId);
                    if (!tag) return null;
                    return (
                      <span key={tag.id} className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium"
                        style={{ backgroundColor: tag.color + '20', color: tag.color }}>
                        {tag.name}
                        <button type="button" onClick={() => setSelectedTags(prev => prev.filter(id => id !== tag.id))}
                          className="hover:text-red-500"><X className="h-3 w-3" /></button>
                      </span>
                    );
                  })}
                  <div className="relative">
                    <button type="button" onClick={() => setShowTagPicker(!showTagPicker)}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs text-gray-400 border border-dashed border-gray-300 hover:border-gray-400 hover:text-gray-500">
                      <Tag className="h-3 w-3" /> Add Tag
                    </button>
                    {showTagPicker && (
                      <div className="absolute z-50 mt-1 left-0 w-52 bg-white rounded-lg border border-gray-200 shadow-xl">
                        <div className="max-h-[200px] overflow-y-auto">
                          {allTags.filter(t => !selectedTags.includes(t.id)).length === 0 && (
                            <p className="text-xs text-gray-400 p-3 text-center">No more tags</p>
                          )}
                          {allTags.filter(t => !selectedTags.includes(t.id)).map(tag => (
                            <button key={tag.id} type="button" onClick={() => { setSelectedTags(prev => [...prev, tag.id]); setShowTagPicker(false); }}
                              className="w-full text-left px-3 py-2 hover:bg-gray-50 flex items-center gap-2 text-xs">
                              <span className="h-3 w-3 rounded-full flex-shrink-0" style={{ backgroundColor: tag.color }} />
                              {tag.name}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Form */}
              {screenLayout ? (
                <ScreenRenderer containerClassName="grid grid-cols-2 gap-4"
                  layout={screenLayout}
                  fields={fields}
                  fieldData={fieldData}
                  onChange={handleFieldChange}
                  errors={{}}
                  readOnly={false}
                />
              ) : fields.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-8">No fields configured.</p>
              ) : (
                <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                  {fields.filter(f => f.field_type !== 'FILE' && f.field_type !== 'RECORD_LINK').map(field => (
                    <div key={field.id} className={['TEXTAREA','RICH_TEXT'].includes(field.field_type) ? 'col-span-2' : ''}>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {field.label}
                        {field.is_required && <span className="text-red-500 ml-0.5">*</span>}
                      </label>
                      {field.field_type === 'TEXTAREA' || field.field_type === 'RICH_TEXT' ? (
                        <textarea value={fieldData[field.id] || ''}
                          onChange={e => handleFieldChange(field.id, e.target.value)} rows={3}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500" />
                      ) : field.field_type === 'DROPDOWN' && field.config?.options ? (
                        <select value={fieldData[field.id] || ''}
                          onChange={e => handleFieldChange(field.id, e.target.value)}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 bg-white">
                          <option value="">Select...</option>
                          {field.config.options.map(o => <option key={o.value || o} value={o.value || o}>{o.label || o}</option>)}
                        </select>
                      ) : field.field_type === 'TOGGLE' ? (
                        <label className="flex items-center gap-2">
                          <input type="checkbox" checked={!!fieldData[field.id]}
                            onChange={e => handleFieldChange(field.id, e.target.checked)}
                            className="rounded text-brand-600" />
                          <span className="text-sm text-gray-600">{field.label}</span>
                        </label>
                      ) : field.field_type === 'DATE' ? (
                        <input type="date" value={fieldData[field.id] || ''}
                          onChange={e => handleFieldChange(field.id, e.target.value)}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500" />
                      ) : (
                        <input type={field.field_type === 'EMAIL' ? 'email' : field.field_type === 'NUMBER' || field.field_type === 'CURRENCY' ? 'number' : 'text'}
                          value={fieldData[field.id] || ''}
                          onChange={e => handleFieldChange(field.id, e.target.value)}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500" />
                      )}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        <div className="flex justify-end gap-2 px-6 py-4 border-t border-gray-200 flex-shrink-0">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">Cancel</button>
          <button onClick={handleSave} disabled={saving || loading}
            className="flex items-center gap-2 px-4 py-2 bg-brand-600 text-white text-sm font-medium rounded-lg hover:bg-brand-700 disabled:opacity-50">
            {saving ? <Loader className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            {saving ? 'Creating...' : 'Create & Link'}
          </button>
        </div>

        <style>{`
          @keyframes modalIn { from { opacity:0; transform: scale(0.95) translateY(10px); } to { opacity:1; transform: scale(1) translateY(0); } }
          .animate-modal-in { animation: modalIn 0.2s ease-out; }
        `}</style>
      </div>
    </div>
  );
}

export default function LookupField({ field, value, onChange, recordId, readOnly }) {
  const config = field.config || {};
  const targetTypeId = config.target_record_type_id;
  const targetTypeName = config.target_type_name || 'Record';
  const allowMultiple = config.allow_multiple || false;

  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selected, setSelected] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const debounceRef = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (!value || !targetTypeId) return;
    const ids = Array.isArray(value) ? value : [value];
    if (ids.length === 0) return;
    relationsApi.search(targetTypeId, '').then(data => {
      const matched = (data.records || []).filter(r => ids.includes(r.id));
      setSelected(matched);
    }).catch(() => {});
  }, []);

  const doSearch = useCallback((q) => {
    if (!targetTypeId) return;
    setSearching(true);
    relationsApi.search(targetTypeId, q, recordId).then(data => {
      setResults(data.records || []);
    }).catch(() => {}).finally(() => setSearching(false));
  }, [targetTypeId, recordId]);

  const handleInputChange = (val) => {
    setQuery(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doSearch(val), 250);
  };

  const handleFocus = () => {
    setShowDropdown(true);
    if (results.length === 0) doSearch(query);
  };

  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setShowDropdown(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const linkRecord = async (record) => {
    if (recordId) {
      try {
        await relationsApi.create({
          source_record_id: recordId,
          target_record_id: record.id,
          field_definition_id: field.id,
        });
      } catch (err) {
        if (err.response?.status !== 409) {
          toast.error('Failed to link record');
          return;
        }
      }
    }
    if (allowMultiple) {
      const prev = Array.isArray(value) ? value : value ? [value] : [];
      if (!prev.includes(record.id)) {
        onChange([...prev, record.id]);
        setSelected(s => [...s, record]);
      }
    } else {
      onChange(record.id);
      setSelected([record]);
    }
  };

  const handleSelect = async (record) => {
    await linkRecord(record);
    setQuery('');
    setShowDropdown(false);
  };

  const handleCreated = async (record) => {
    setShowCreateModal(false);
    await linkRecord(record);
    setQuery('');
    setShowDropdown(false);
  };

  const handleRemove = async (rec) => {
    if (readOnly) return;
    if (recordId) {
      try {
        const rels = await relationsApi.getForRecord(recordId);
        const rel = (rels.relations || []).find(r =>
          (r.source_record_id === recordId && r.target_record_id === rec.id && r.field_definition_id === field.id) ||
          (r.target_record_id === recordId && r.source_record_id === rec.id && r.field_definition_id === field.id)
        );
        if (rel) await relationsApi.delete(rel.id);
      } catch (err) { /* ignore */ }
    }
    if (allowMultiple) {
      const prev = Array.isArray(value) ? value : [];
      onChange(prev.filter(id => id !== rec.id));
      setSelected(s => s.filter(r => r.id !== rec.id));
    } else {
      onChange(null);
      setSelected([]);
    }
  };

  if (!targetTypeId) {
    return (
      <div className="border-2 border-dashed border-yellow-300 rounded-lg p-3 text-xs text-yellow-600">
        This lookup field needs configuration — set a target record type in field settings.
      </div>
    );
  }

  return (
    <div ref={dropdownRef} className="relative">
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-2">
          {selected.map(rec => (
            <div key={rec.id} className="flex items-center gap-1.5 px-2.5 py-1.5 bg-brand-50 border border-brand-200 rounded-lg text-xs group">
              <span className="font-medium text-brand-700">{rec.title || rec.reference_number}</span>
              {rec.reference_number && rec.title && (
                <span className="text-brand-400 text-[10px]">{rec.reference_number}</span>
              )}
              {rec.stage_name && (
                <span className="px-1.5 py-0.5 rounded-full text-[9px] font-medium"
                  style={{ backgroundColor: (rec.stage_color || '#6b7280') + '20', color: rec.stage_color || '#6b7280' }}>
                  {rec.stage_name}
                </span>
              )}
              <a href={`/records/${rec.record_type_slug}/${rec.id}`} target="_blank" rel="noreferrer"
                className="text-brand-400 hover:text-brand-600" onClick={e => e.stopPropagation()}>
                <ExternalLink className="h-3 w-3" />
              </a>
              {!readOnly && (
                <button onClick={() => handleRemove(rec)}
                  className="text-brand-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity">
                  <X className="h-3 w-3" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {!readOnly && (allowMultiple || selected.length === 0) && (
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-gray-400" />
          <input type="text" value={query} onChange={e => handleInputChange(e.target.value)} onFocus={handleFocus}
            placeholder={`Search ${targetTypeName}...`}
            className="w-full pl-8 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500" />
        </div>
      )}

      {showDropdown && (
        <div className="absolute z-50 mt-1 w-full bg-white rounded-lg border border-gray-200 shadow-xl max-h-[280px] overflow-y-auto">
          <button onClick={() => { setShowDropdown(false); setShowCreateModal(true); }}
            className="w-full flex items-center gap-2 px-3 py-2.5 text-sm font-medium text-brand-600 hover:bg-brand-50 border-b border-gray-100">
            <Plus className="h-4 w-4" /> Create New {targetTypeName}
          </button>

          {searching ? (
            <p className="text-xs text-gray-400 p-3 text-center">Searching...</p>
          ) : results.length === 0 ? (
            <p className="text-xs text-gray-400 p-3 text-center">No records found</p>
          ) : (
            results.map(rec => {
              const alreadySelected = Array.isArray(value) ? value.includes(rec.id) : value === rec.id;
              return (
                <button key={rec.id} onClick={() => !alreadySelected && handleSelect(rec)}
                  disabled={alreadySelected}
                  className={`w-full text-left px-3 py-2.5 hover:bg-gray-50 border-b border-gray-50 last:border-0 flex items-center gap-2 ${alreadySelected ? 'opacity-40' : ''}`}>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{rec.title || '(untitled)'}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-[10px] text-gray-400 font-mono">{rec.reference_number}</span>
                      {rec.stage_name && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full font-medium"
                          style={{ backgroundColor: (rec.stage_color || '#6b7280') + '20', color: rec.stage_color || '#6b7280' }}>
                          {rec.stage_name}
                        </span>
                      )}
                    </div>
                  </div>
                  {alreadySelected ? <span className="text-[10px] text-gray-400">linked</span> : <Plus className="h-3.5 w-3.5 text-gray-400" />}
                </button>
              );
            })
          )}
        </div>
      )}

      {showCreateModal && (
        <CreateModal
          targetTypeId={targetTypeId}
          targetTypeName={targetTypeName}
          onCreated={handleCreated}
          onClose={() => setShowCreateModal(false)}
        />
      )}
    </div>
  );
}
